import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { LuArrowLeft, LuArrowRight } from 'react-icons/lu';
import styled from 'styled-components';

import {
	VideoProjectCreationProvider,
	useVideoProjectCreation,
	DEFAULT_CONFIDENCE,
} from '@contexts/VideoProjectCreation.context';
import { useNewProject } from '@contexts/NewProject.context';
import { extractVideoMetadata, formatFileSize, isTransportStream } from '@utils/video/helpers';
import { previewTransportStream } from '@utils/video/tsPreview';
import { modelForSubject } from '@config/videoDetectionModelConfig';
import { unsupportedReportChars } from '@utils/video/reportText';
import { secondsLeft } from '@routes/userRoutes/projects/videoProject/flow/jobEta';

import VideoFlowShell from '@routes/userRoutes/projects/videoProject/flow/VideoFlowShell';
import StepRail from '@routes/userRoutes/projects/videoProject/flow/StepRail';
import { FilmStrip } from '@routes/userRoutes/projects/videoProject/flow/Strips';
import RangeOverlay from '@routes/userRoutes/projects/videoProject/flow/RangeOverlay';
import OverlayPlayer from '@routes/userRoutes/projects/videoProject/components/overlay/OverlayPlayer';
import { formatDuration } from '@routes/userRoutes/projects/videoProject/flow/ProjectCards';
import {
	FlowPanel,
	PanelTitle,
	SectionTitle,
	Muted,
	Small,
	Pills,
	Pill,
	KeyValues,
	FlowButton,
	ButtonRow,
	ProgressBar,
	SpreadLine,
	Note,
	Rule,
	Foot,
	UNDER_PANEL_MIN_HEIGHT,
} from '@routes/userRoutes/projects/videoProject/flow/primitives';
import FlowDropZone from './flow/FlowDropZone';
import SuggestionCard from './flow/SuggestionCard';
import useCountingSuggestion from './flow/useCountingSuggestion';
import {
	ProjectDetailsCard,
	COUNT_MODES,
	CountModeCards,
	SubjectField,
	SensitivityField,
	TrimField,
	modeOf,
} from './flow/WizardParts';

/**
 * New video project in the three-column flow (docs/video-flow-design/VIDEO_FLOW_DESIGN.html, screens 1-3):
 * select the video, configure counting on the video itself, upload. The project logic (create, multipart upload,
 * start the job, archive on failure) stays in VideoProjectCreation.context.
 * Wizard stages are 3 (select) · 4 (configure) · 5 (upload) · 6 (processing did not start).
 */

const STAGE_STEP = { 3: 0, 4: 1, 5: 2, 6: 3 };

// The center column has a fixed height: the picture is as wide as that height allows once the player's control bar
// (measured, as it wraps on a large font) and the strip's least height are kept free, so the strip under it is never
// cut off; the strip takes the slack. Only the picture shrinks: the control bar keeps the column's width, else a
// narrower picture would wrap the bar, which would narrow the picture further.
const CentreFit = styled.div`
	flex: 1;
	min-height: 0;
	display: flex;
	flex-direction: column;
	gap: 0.75rem;
	container-type: size;
	> :first-child {
		flex: none;
	}
	> :first-child > :first-child {
		width: min(
			100%,
			calc((100cqh - (${({ $reserve }) => $reserve})) * ${({ $ratio }) => $ratio})
		);
		margin-inline: auto;
	}
	@media (max-width: 899.98px) {
		flex: none;
		container-type: normal;
		> :first-child > :first-child {
			width: 100%;
		}
	}
`;

const ASSUMED_FPS = 30; // the browser does not expose a video's frame rate; only used to map the line editor

const toLine = d => (d ? { start: d.start_point, end: d.end_point } : null);
const fromLine = l => ({
	start_point: [Math.round(l.start[0]), Math.round(l.start[1])],
	end_point: [Math.round(l.end[0]), Math.round(l.end[1])],
});

/** OverlayPlayer needs a track index to map the line editor onto the video; an empty one does for a raw video. */
const emptyIndex = meta => {
	const n = Math.max(1, Math.round((meta?.duration || 1) * ASSUMED_FPS));
	return {
		fps: ASSUMED_FPS,
		width: meta.width,
		height: meta.height,
		nFrames: n,
		tracks: [],
		perFrame: new Int32Array(n + 1),
		trackOf: [],
		pointOf: [],
	};
};

/** Reads duration and size from the local file once, the same way the old player did. */
const useLocalMetadata = (videoURL, videoFile, setVideoMetadata, setStills) => {
	useEffect(() => {
		if (!videoURL) return undefined;
		if (isTransportStream(videoFile)) {
			// the browser cannot play a .ts: read its length, size and still frames from the file itself
			let cancelled = false;
			setStills(null);
			const file = {
				fileSize: videoFile.size,
				fileName: videoFile.name,
				fileType: videoFile.type,
			};
			previewTransportStream(videoFile)
				.catch(() => null)
				.then(p => {
					if (cancelled) return;
					setStills(p);
					setVideoMetadata(
						p
							? {
									duration: p.duration,
									width: p.width,
									height: p.height,
									aspectRatio: p.width / p.height,
									...file,
								}
							: { duration: 0, width: 0, height: 0, aspectRatio: 0, ...file }
					);
				});
			return () => {
				cancelled = true;
			};
		}
		const v = document.createElement('video');
		v.preload = 'metadata';
		v.muted = true;
		const file = {
			fileSize: videoFile?.size || 0,
			fileName: videoFile?.name || '',
			fileType: videoFile?.type || '',
		};
		const ok = () => setVideoMetadata({ ...extractVideoMetadata(v), ...file });
		const fail = () =>
			setVideoMetadata({ duration: 0, width: 0, height: 0, aspectRatio: 0, ...file });
		v.addEventListener('loadedmetadata', ok);
		v.addEventListener('error', fail);
		v.src = videoURL;
		return () => {
			v.removeEventListener('loadedmetadata', ok);
			v.removeEventListener('error', fail);
			v.removeAttribute('src');
			v.load();
		};
	}, [videoURL, videoFile, setVideoMetadata, setStills]);
};

/** Upload speed and time left from the progress percentage. */
const useUploadRate = (progress, totalBytes) => {
	const first = useRef(null);
	const [rate, setRate] = useState(null);
	useEffect(() => {
		if (!progress) {
			first.current = null;
			return;
		}
		const now = Date.now();
		if (!first.current) first.current = { t: now, p: progress };
		const dt = (now - first.current.t) / 1000;
		if (dt > 2) setRate(((progress - first.current.p) / 100) * (totalBytes / dt));
	}, [progress, totalBytes]);
	if (!rate) return null;
	const left = ((100 - progress) / 100) * totalBytes;
	return { mbps: rate / 1e6, secondsLeft: left / rate };
};

const minutes = s => (s < 90 ? `${Math.max(1, Math.round(s))} s` : `${Math.round(s / 60)} min`);

const BEFORE_YOU_START = [
	['Camera straight down', 'Tilted views count fewer animals'],
	['Steady altitude', '40–80 m works well for reindeer'],
	['Trim before upload', 'Pick the part to analyse in the next step'],
];

const VideoProjectWizardContent = () => {
	const navigate = useNavigate();
	const { setMode } = useNewProject();
	const { watch } = useFormContext();
	const projectName = (watch('projectName') || '').trim();
	// the name card warns about characters the PDF report cannot write; the job would fail on them
	const nameOk = !!projectName && !unsupportedReportChars(projectName).length;
	let nameHint = 'Next you choose how to count and which part of the video to analyse.';
	if (!projectName) nameHint = 'Give the project a name (left) to continue.';
	else if (!nameOk) nameHint = 'Replace the unsupported characters in the project name (left).';
	const {
		currentStage,
		nextStage,
		previousStage,
		canProceedFromStage,
		videoFile,
		setVideoFile,
		videoURL,
		videoMetadata,
		setVideoMetadata,
		configuration,
		setConfiguration,
		uploadProgress,
		isUploading,
		isProcessing,
		createProjectAndUpload,
		retryProcessing,
		projectUuid,
		error: uploadError,
	} = useVideoProjectCreation();

	const [preview, setPreview] = useState(null);
	useLocalMetadata(videoURL, videoFile, setVideoMetadata, setPreview);
	const stills = preview?.frames || null;
	const suggest = useCountingSuggestion({ videoFile, videoURL, videoMetadata, stills });
	const suggestedMode = COUNT_MODES.find(m => m.id === suggest.suggestion?.mode) || null;
	const playerRef = useRef(null);
	// the height of the player's control bar with its margin, which the video fit has to leave room for
	const [chrome, setChrome] = useState(0);
	useEffect(() => {
		const controls = playerRef.current?.controls?.();
		if (!controls) return undefined;
		const ro = new ResizeObserver(() =>
			setChrome(
				Math.round(
					controls.getBoundingClientRect().height +
						parseFloat(getComputedStyle(controls).marginTop || 0)
				)
			)
		);
		ro.observe(controls);
		return () => ro.disconnect();
		// the player (and so its control bar) is only there once a video is chosen
	}, [videoFile]);
	const duration = videoMetadata?.duration || 0;
	const canPreview = duration > 0 && videoMetadata?.width > 0;
	const index = useMemo(
		() => (canPreview ? emptyIndex(videoMetadata) : null),
		[canPreview, videoMetadata]
	);
	const mode = modeOf(configuration);
	const range = configuration.timeRange;

	// default range: the whole clip
	useEffect(() => {
		if (duration && !configuration.timeRange)
			setConfiguration(c => ({
				...c,
				timeRange: { start_seconds: 0, end_seconds: Math.round(duration * 100) / 100 },
			}));
	}, [duration, configuration.timeRange, setConfiguration]);

	const chooseMode = useCallback(
		m => {
			setConfiguration(c => ({
				...c,
				countingScenario: m.scenario,
				countingMethod: m.method,
				// a line across the middle to start from; drag it or its ends, or drag on the video for a new one
				virtualLineDefinition:
					m.method === 'line_based'
						? c.virtualLineDefinition ||
							(videoMetadata?.height
								? fromLine({
										start: [
											videoMetadata.width * 0.03,
											videoMetadata.height / 2,
										],
										end: [videoMetadata.width * 0.97, videoMetadata.height / 2],
									})
								: null)
						: null,
			}));
		},
		[setConfiguration, videoMetadata]
	);

	// the suggested way to count is picked for the user once, if they have not picked one themselves
	const appliedFor = useRef(null);
	useEffect(() => {
		if (currentStage !== 4 || !suggestedMode || appliedFor.current === videoFile) return;
		appliedFor.current = videoFile;
		if (!modeOf(configuration) && !(suggestedMode.id === 'line' && !canPreview))
			chooseMode(suggestedMode);
	}, [currentStage, suggestedMode, videoFile, configuration, canPreview, chooseMode]);

	const analysed = range ? range.end_seconds - range.start_seconds : duration;
	// what to count: the user's word, else the AI's suggestion, else reindeer
	const subject = configuration.subject ?? suggest.suggestion?.subject ?? 'reindeer';
	const search = modelForSubject(subject).value === 'SEARCH';
	const estimate = analysed
		? Math.round(
				secondsLeft({
					analysedSeconds: analysed,
					megapixels: videoMetadata?.width
						? (videoMetadata.width * videoMetadata.height) / 1e6
						: undefined,
					search,
				}) / 60
			)
		: null;
	const rate = useUploadRate(uploadProgress, videoFile?.size || 0);
	const step = STAGE_STEP[currentStage] ?? 0;

	const startUpload = async () => {
		nextStage();
		await createProjectAndUpload();
	};

	const retry = async () => {
		if (await retryProcessing()) navigate(`/video-projects/${projectUuid}`, { replace: true });
	};

	const summaries = {
		0: videoFile?.name,
		1:
			step > 1 && mode
				? [
						mode.title.split(' · ')[0],
						range &&
							`${formatDuration(range.start_seconds)}–${formatDuration(range.end_seconds)}`,
					]
						.filter(Boolean)
						.join(' · ')
				: null,
		2:
			currentStage === 5
				? `${Math.floor(uploadProgress)} %${rate ? ` · ~${minutes(rate.secondsLeft)} left` : ''}`
				: null,
		3: currentStage === 6 ? 'Did not start' : null,
	};

	const left = (
		<>
			<StepRail
				current={step}
				currentState={currentStage === 6 ? 'stopped' : 'current'}
				summaries={summaries}
			/>
			<ProjectDetailsCard
				disabled={currentStage >= 5}
				onChangeType={currentStage === 3 ? () => setMode(null) : null}
			/>
		</>
	);

	// ---- centre
	let centre;
	if (!videoFile) centre = <FlowDropZone onFile={setVideoFile} />;
	else {
		const editing = currentStage === 4 && mode?.method === 'line_based' && index;
		const line =
			mode?.method === 'line_based' ? toLine(configuration.virtualLineDefinition) : null;
		const strip = (currentStage === 4 && duration) || canPreview;
		centre = (
			<CentreFit
				$ratio={canPreview ? videoMetadata.width / videoMetadata.height : 16 / 9}
				$reserve={
					strip ? `${chrome}px + 0.75rem + ${UNDER_PANEL_MIN_HEIGHT}px` : `${chrome}px`
				}>
				<OverlayPlayer
					ref={playerRef}
					src={videoURL}
					stills={stills}
					stillsDuration={duration}
					index={index}
					line={index ? line : null}
					lineEditable={!!editing}
					onLineChange={l =>
						setConfiguration(c => ({ ...c, virtualLineDefinition: fromLine(l) }))
					}
					showDetections={false}
					aspect={
						videoMetadata?.aspectRatio
							? `${videoMetadata.width} / ${videoMetadata.height}`
							: undefined
					}>
					{currentStage >= 5 && (
						<div
							style={{
								position: 'absolute',
								inset: 0,
								background: 'rgba(8, 10, 8, 0.55)',
								display: 'grid',
								placeItems: 'center',
							}}>
							<FlowPanel
								as="div"
								style={{
									width: 380,
									maxWidth: '90%',
									padding: '1.375rem 1.625rem',
								}}>
								<PanelTitle>
									{uploadProgress >= 100
										? 'Starting the analysis…'
										: 'Uploading video'}
								</PanelTitle>
								<ProgressBar value={uploadProgress} label="Upload progress" />
								<SpreadLine>
									<span>
										{formatFileSize(
											((videoFile?.size || 0) * uploadProgress) / 100
										)}{' '}
										of {formatFileSize(videoFile?.size || 0)}
									</span>
									<span>
										{rate
											? `${rate.mbps.toFixed(0)} MB/s · ~${minutes(rate.secondsLeft)} left`
											: ''}
									</span>
								</SpreadLine>
								<Small>
									Keep this tab open until the upload finishes. The analysis
									starts by itself.
								</Small>
							</FlowPanel>
						</div>
					)}
				</OverlayPlayer>
				{currentStage === 4 && duration ? (
					<FilmStrip
						src={videoURL}
						stills={stills}
						title="Part to analyse · trim"
						right={
							range
								? `Drag the handles · ${formatDuration(range.start_seconds)} – ${formatDuration(range.end_seconds)} (${Math.round(analysed)} s)`
								: null
						}>
						{range && (
							<RangeOverlay
								duration={duration}
								start={range.start_seconds}
								end={range.end_seconds}
								onChange={({ start, end }) => {
									setConfiguration(c => ({
										...c,
										timeRange: { start_seconds: start, end_seconds: end },
									}));
									playerRef.current?.seekTo(
										start !== range.start_seconds ? start : end
									);
								}}
							/>
						)}
					</FilmStrip>
				) : canPreview ? (
					<FilmStrip
						src={videoURL}
						stills={stills}
						right={`0:00 – ${formatDuration(duration)}`}
					/>
				) : null}
			</CentreFit>
		);
	}

	// ---- right
	let right;
	if (currentStage === 3 && !videoFile)
		right = (
			<FlowPanel $fill>
				<PanelTitle>Before you start</PanelTitle>
				<Muted>
					The model counts the animals it can see from above. A few things make a big
					difference:
				</Muted>
				{BEFORE_YOU_START.map(([t, h]) => (
					<div
						key={t}
						style={{
							border: '1px solid #343834',
							borderRadius: 12,
							padding: '0.7rem 0.75rem',
						}}>
						<SectionTitle>{t}</SectionTitle>
						<Small>{h}</Small>
					</div>
				))}
				<Foot>
					Your video stays private to your organisation.
					<br />
					Processing takes roughly 5× the length of the part you analyse.
				</Foot>
			</FlowPanel>
		);
	else if (currentStage === 3)
		right = (
			<FlowPanel $fill>
				<PanelTitle>{videoFile.name}</PanelTitle>
				<Pills>
					<Pill>
						{videoMetadata ? (canPreview ? 'Ready' : 'No preview') : 'Reading…'}
					</Pill>
					{videoMetadata?.height >= 2160 && <Pill $variant="ghost">4K</Pill>}
				</Pills>
				<KeyValues
					items={[
						['Duration', duration ? formatDuration(duration) : null],
						[
							'Resolution',
							videoMetadata?.width
								? `${videoMetadata.width} × ${videoMetadata.height}`
								: null,
						],
						['Size', formatFileSize(videoFile.size)],
						['Type', videoFile.type || null],
						...(preview?.flightData ? [['Flight data', 'Found · you get a map']] : []),
					]}
				/>
				{stills && (
					<Note>
						A .ts video does not play in the browser, so these are still frames from it.
						The analysis uses every frame.
					</Note>
				)}
				{videoMetadata && !canPreview && (
					<Note>
						This browser cannot play the video, so you cannot draw a counting line. It
						can still be uploaded and counted.
					</Note>
				)}
				<SuggestionCard
					status={suggest.status}
					suggestion={suggest.suggestion}
					subject={configuration.subject}
					onSubject={w => setConfiguration(c => ({ ...c, subject: w }))}
					modeTitle={suggestedMode?.title}
				/>
				<Rule />
				<Muted>{nameHint}</Muted>
				<ButtonRow>
					<FlowButton
						type="button"
						$variant="secondary"
						onClick={() => setVideoFile(null)}>
						Change
					</FlowButton>
					<FlowButton
						type="button"
						onClick={nextStage}
						disabled={!canProceedFromStage(3) || !nameOk || !videoMetadata}>
						Continue <LuArrowRight />
					</FlowButton>
				</ButtonRow>
			</FlowPanel>
		);
	else if (currentStage === 4)
		right = (
			<FlowPanel $fill>
				<PanelTitle>How should we count?</PanelTitle>
				<CountModeCards
					value={mode?.id}
					onChange={chooseMode}
					lineDisabled={!canPreview}
					suggested={suggestedMode?.id}
				/>
				{mode?.method === 'line_based' && (
					<Small>
						Drag the line or its ends on the video, or drag across the video to draw a
						new one. Animals are counted when they cross it.
					</Small>
				)}
				<TrimField
					range={range}
					duration={duration}
					getNow={() => playerRef.current?.now() || 0}
					onChange={r => {
						const moved =
							r.start_seconds !== range.start_seconds
								? r.start_seconds
								: r.end_seconds;
						setConfiguration(c => ({ ...c, timeRange: r }));
						playerRef.current?.seekTo(moved);
					}}
				/>
				<SubjectField
					value={subject}
					onChange={w => setConfiguration(c => ({ ...c, subject: w }))}
				/>
				<SensitivityField
					value={configuration.confidenceThreshold ?? DEFAULT_CONFIDENCE}
					onChange={v => setConfiguration(c => ({ ...c, confidenceThreshold: v }))}
				/>
				{estimate && (
					<Small>
						Estimated processing time:{' '}
						<b style={{ color: '#b4b9b4' }}>about {estimate} minutes</b> for{' '}
						{Math.round(analysed)} s of video.
					</Small>
				)}
				<ButtonRow>
					<FlowButton type="button" $variant="secondary" onClick={previousStage}>
						<LuArrowLeft /> Back
					</FlowButton>
					<FlowButton
						type="button"
						onClick={startUpload}
						disabled={!canProceedFromStage(4) || !nameOk}>
						Upload &amp; analyse
					</FlowButton>
				</ButtonRow>
			</FlowPanel>
		);
	else {
		const settings = (
			<KeyValues
				items={[
					['Counting', mode?.title],
					[
						'Period',
						range
							? `${formatDuration(range.start_seconds)} – ${formatDuration(range.end_seconds)}`
							: 'Whole video',
					],
					['What to count', subject],
					['Model', modelForSubject(subject).label],
					[
						'Sensitivity',
						`${Math.round((configuration.confidenceThreshold ?? DEFAULT_CONFIDENCE) * 100)} %`,
					],
					['Est. time', estimate ? `~${estimate} min` : null],
				]}
			/>
		);
		right =
			currentStage === 6 ? (
				<FlowPanel $fill>
					<PanelTitle>The analysis did not start</PanelTitle>
					<Note $tone="error">
						The video was uploaded, but the analysis could not be started.
					</Note>
					{settings}
					<FlowButton type="button" onClick={retry} disabled={isProcessing}>
						{isProcessing ? 'Starting…' : 'Try again'}
					</FlowButton>
					{projectUuid && (
						<FlowButton
							type="button"
							$variant="secondary"
							onClick={() => navigate(`/video-projects/${projectUuid}`)}>
							Open the project
						</FlowButton>
					)}
				</FlowPanel>
			) : uploadError && !isUploading ? (
				<FlowPanel $fill>
					<PanelTitle>The upload did not finish</PanelTitle>
					<Note $tone="error">
						Nothing was saved. Check the connection and try again.
					</Note>
					{settings}
					<ButtonRow>
						<FlowButton type="button" $variant="secondary" onClick={previousStage}>
							<LuArrowLeft /> Back
						</FlowButton>
						<FlowButton type="button" onClick={createProjectAndUpload}>
							Try again
						</FlowButton>
					</ButtonRow>
				</FlowPanel>
			) : (
				<FlowPanel $fill>
					<PanelTitle>Your settings</PanelTitle>
					<Pills>{mode && <Pill>{mode.title}</Pill>}</Pills>
					{settings}
					<Rule />
					<Muted>
						{isUploading
							? 'The analysis starts as soon as the upload is done; this page then moves to the project. If all GPU machines are busy, the job waits in the queue and you can leave the page once the upload is done.'
							: 'Preparing the upload…'}
					</Muted>
				</FlowPanel>
			);
	}

	return (
		<VideoFlowShell
			crumbs={[
				{ title: 'Projects', link: '/projects' },
				{ title: 'New video project' },
				{ title: projectName || 'Untitled' },
			]}
			left={left}
			centre={centre}
			right={right}
		/>
	);
};

const VideoProjectWizard = () => {
	const { watch } = useFormContext();
	const projectInfo = {
		title: watch('projectName'),
		description: watch('projectDescription'),
		tags: watch('projectTags')?.map(tag => tag.value) || [],
	};
	return (
		<VideoProjectCreationProvider projectInfo={projectInfo}>
			<VideoProjectWizardContent />
		</VideoProjectCreationProvider>
	);
};

export default VideoProjectWizard;
